import type { RebalancePreviewResponse } from "../../lib/api";
import { useCartStore } from "../../lib/cartStore";
import { cn } from "../../lib/cn";
import { formatPercent } from "../../lib/format";

export function DecisionBanner({ data }: { data: RebalancePreviewResponse }) {
	const yes = data.decision.shouldRebalance;
	const relaxed = data.diagnostics.status.includes("relaxed turnover");
	const add = useCartStore((s) => s.add);
	const intents = useCartStore((s) => s.intents);
	const alreadyInCart = intents.some((i) => i.kind === "rebalance");
	const hasTrades = Object.values(data.deltaValues.assetDeltaValuesRaw).some(
		(v) => BigInt(v) !== 0n,
	);

	const prepareTx = () => {
		add({
			kind: "rebalance",
			deltas: data.deltaValues.assetDeltaValuesRaw,
			apyDeltaPct: formatPercent(data.decision.expectedApyDelta),
			netBenefitUsdcRaw: data.decision.expectedNetBenefitRaw,
		});
	};

	// Label varies by decision so the affordance is honest about
	// intent — recommending vs. overriding the optimizer.
	const buttonLabel = alreadyInCart
		? "Update tx plan"
		: yes
			? "Prepare tx"
			: "Prepare anyway";

	return (
		<div
			className={cn(
				"space-y-2 rounded-lg border p-4",
				yes
					? "border-emerald-700/50 bg-emerald-950/30"
					: "border-slate-800 bg-slate-900/40",
			)}
		>
			<div className="flex items-center justify-between gap-3">
				<p
					className={cn(
						"text-base font-semibold",
						yes ? "text-emerald-300" : "text-slate-200",
					)}
				>
					{yes ? "Rebalance recommended" : "Hold — no rebalance"}
				</p>
				<div className="flex items-center gap-2">
					<span
						className={cn(
							"rounded-full px-3 py-1 text-xs",
							yes
								? "bg-emerald-500/10 text-emerald-300"
								: "bg-slate-800 text-slate-300",
						)}
					>
						{data.decision.reason}
					</span>
					<button
						type="button"
						onClick={prepareTx}
						disabled={!hasTrades}
						title={
							hasTrades
								? yes
									? "Stage this rebalance in the transactions plan"
									: "Optimizer says hold — stage anyway for manual review"
								: "No trades to stage"
						}
						className={
							yes
								? "rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-40"
								: "rounded-md border border-amber-700/60 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-amber-200 transition hover:border-amber-500 hover:text-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
						}
					>
						{buttonLabel}
					</button>
				</div>
			</div>
			{relaxed && (
				<p className="rounded border border-amber-900/40 bg-amber-950/30 px-3 py-2 text-[11px] text-amber-200/80">
					Turnover cap relaxed for this cycle — the starting portfolio is
					outside the policy caps and a single-cycle compliant move wasn't
					feasible. Per-asset, per-protocol, and risk-bucket caps still hold;
					only the L1 turnover budget was lifted.
				</p>
			)}
		</div>
	);
}
